import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  Select,
  NumberInput,
} from "@mantine/core";
import { IconCheck, IconDownload, IconPrinter, IconUpload, IconX } from "@tabler/icons-react";
import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { repositoryPos, repositoryMdm } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { LabelTemplate, ProductLabel } from "./LabelTemplate";

const PRINTER_CONFIGS: Record<string, {
  marginLeft: number;
  marginTop: number;
  columnGap: number;
  rowGap: number;
  pageWidth: number;
  pageHeight: number;
}> = {
  "Epson": {
    marginLeft: 6,
    marginTop: 2,
    columnGap: 4,
    rowGap: 2,
    pageWidth: 210,
    pageHeight: 165,
  },
  "Canon 2900": {
    marginLeft: 6,
    marginTop: 3.5,
    columnGap: 4.2,
    rowGap: 1.7,
    pageWidth: 210,
    pageHeight: 165,
  }
};

const PrintProductLabel = () => {
  const [labels, setLabels] = useState<ProductLabel[]>([]);
  const [batchCode, setBatchCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [printerType, setPrinterType] = useState<string>("Epson");
  const [marginLeft, setMarginLeft] = useState<number | string>(PRINTER_CONFIGS["Epson"].marginLeft);
  const [marginTop, setMarginTop] = useState<number | string>(PRINTER_CONFIGS["Epson"].marginTop);
  const [columnGap, setColumnGap] = useState<number | string>(PRINTER_CONFIGS["Epson"].columnGap);
  const [rowGap, setRowGap] = useState<number | string>(PRINTER_CONFIGS["Epson"].rowGap);
  const [pageWidth, setPageWidth] = useState<number | string>(PRINTER_CONFIGS["Epson"].pageWidth);
  const [pageHeight, setPageHeight] = useState<number | string>(PRINTER_CONFIGS["Epson"].pageHeight);

  const handlePrinterChange = (val: string | null) => {
    if (val && PRINTER_CONFIGS[val]) {
      setPrinterType(val);
      const config = PRINTER_CONFIGS[val];
      setMarginLeft(config.marginLeft);
      setMarginTop(config.marginTop);
      setColumnGap(config.columnGap);
      setRowGap(config.rowGap);
      setPageWidth(config.pageWidth);
      setPageHeight(config.pageHeight);
    }
  };

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "InTemSanPham",
    onAfterPrint: () => console.log("Printed successfully"),
  });

  const downloadSampleExcel = () => {
    const ws = XLSX.utils.json_to_sheet([
      { "Mã SP": "CSHA0002", "Số lượng cần in": 15 },
      { "Mã SP": "MEFU0008", "Số lượng cần in": 15 },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DanhSachInTem");
    XLSX.writeFile(wb, "Mau_In_Tem_San_Pham.xlsx");
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws) as any[];

        const newLabels: ProductLabel[] = data.map((row) => ({
          itemCode: row["Mã SP"] || "",
          quantity: parseInt(row["Số lượng cần in"]) || 0,
          itemName: "",
          price: 0,
          status: "pending" as const,
        })).filter(x => x.itemCode && x.quantity > 0);

        setLabels(newLabels);
        NotificationExtension.Success("Import file thành công!");
      } catch (error) {
        console.error(error);
        NotificationExtension.Fails("File không hợp lệ hoặc sai định dạng!");
      }
    };
    reader.readAsBinaryString(file);
    // Reset input
    e.target.value = "";
  };

  const handleFetchData = async () => {
    if (labels.length === 0) {
      NotificationExtension.Fails("Chưa có dữ liệu. Vui lòng import file!");
      return;
    }

    setIsLoading(true);
    let updatedLabels = [...labels];

    for (let i = 0; i < updatedLabels.length; i++) {
      const item = updatedLabels[i];
      if (item.status === "success") continue; // Skip already fetched

      try {
        updatedLabels[i].status = "loading";
        setLabels([...updatedLabels]);

        const url = `/api/v1/TblTemItem/get-label-info?sku=${item.itemCode}`;
        const res = await repositoryMdm.get<MessageResponse<any>>(url);

        if (res && res.success && res.data) {
          const product = res.data;
          updatedLabels[i].itemName = product.productName || "Không có tên";
          updatedLabels[i].price = product.price || 0;
          updatedLabels[i].status = "success";
        } else {
          updatedLabels[i].status = "error";
          updatedLabels[i].errorMsg = "Không tìm thấy sản phẩm";
        }
      } catch (error) {
        updatedLabels[i].status = "error";
        updatedLabels[i].errorMsg = "Lỗi kết nối";
      }
      setLabels([...updatedLabels]);
    }
    setIsLoading(false);
  };

  // Assign batchCode to all labels right before rendering or passing to template
  const labelsToPrint = labels.map(l => ({ ...l, batchCode: batchCode }));

  return (
    <Box>
      <Flex justify={"space-between"} align={"center"} w={"100%"} mb="md">
        <_breadcrumb />
      </Flex>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Flex justify="space-between" align="center" mb="md">
          <Title order={3}>Cấu hình In Tem</Title>
          <Group>
            <Button
              variant="outline"
              color="gray"
              leftSection={<IconDownload size={16} />}
              onClick={downloadSampleExcel}
            >
              Tải file mẫu
            </Button>
            <Button
              component="label"
              leftSection={<IconUpload size={16} />}
              color="blue"
            >
              Import Excel
              <input
                type="file"
                accept=".xlsx, .xls"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            </Button>
          </Group>
        </Flex>

        <Flex align="center" gap="md" mb="xl" wrap="wrap">
          <Select
            label="Loại máy in"
            data={Object.keys(PRINTER_CONFIGS)}
            value={printerType}
            onChange={handlePrinterChange}
            style={{ width: "160px" }}
          />
          <TextInput
            label="Mã lô / Số hóa đơn"
            placeholder="Ví dụ: HDKM0022"
            value={batchCode}
            onChange={(e) => setBatchCode(e.target.value)}
            style={{ width: "200px" }}
          />
          <NumberInput
            label="Khổ ngang giấy (mm)"
            value={pageWidth}
            onChange={setPageWidth}
            min={1}
            style={{ width: "160px" }}
          />
          <NumberInput
            label="Khổ dọc giấy (mm)"
            value={pageHeight}
            onChange={setPageHeight}
            min={1}
            style={{ width: "160px" }}
          />
          <NumberInput
            label="Lề trái (mm)"
            placeholder="Tự động"
            value={marginLeft}
            onChange={setMarginLeft}
            style={{ width: "100px" }}
          />
          <NumberInput
            label="Lề trên (mm)"
            placeholder="Tự động"
            value={marginTop}
            onChange={setMarginTop}
            style={{ width: "100px" }}
          />
          <NumberInput
            label="Khoảng cách ngang (mm)"
            value={columnGap}
            onChange={setColumnGap}
            min={0}
            style={{ width: "170px" }}
          />
          <NumberInput
            label="Khoảng cách dọc (mm)"
            value={rowGap}
            onChange={setRowGap}
            min={0}
            style={{ width: "160px" }}
          />
          <Box style={{ flexGrow: 1 }} />
          <Button
            color="indigo"
            onClick={handleFetchData}
            loading={isLoading}
            mt={24}
          >
            Chạy (Lấy thông tin SP)
          </Button>
          <Button
            color="green"
            leftSection={<IconPrinter size={16} />}
            onClick={() => handlePrint()}
            disabled={labels.filter((l) => l.status === "success").length === 0}
            mt={24}
          >
            In Tem
          </Button>
        </Flex>

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>STT</Table.Th>
              <Table.Th>Mã SP</Table.Th>
              <Table.Th>Số lượng cần in</Table.Th>
              <Table.Th>Tên Sản Phẩm (Tự động lấy)</Table.Th>
              <Table.Th>Giá Tiền (Tự động lấy)</Table.Th>
              <Table.Th>Trạng thái</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {labels.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                  <Text color="dimmed">Chưa có dữ liệu. Vui lòng tải file mẫu và Import danh sách.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              labels.map((row, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{index + 1}</Table.Td>
                  <Table.Td>
                    <Text fw={500}>{row.itemCode}</Text>
                  </Table.Td>
                  <Table.Td>{row.quantity}</Table.Td>
                  <Table.Td>{row.itemName || "-"}</Table.Td>
                  <Table.Td>
                    {row.price ? new Intl.NumberFormat("vi-VN").format(row.price) + " đ" : "-"}
                  </Table.Td>
                  <Table.Td>
                    {row.status === "pending" && <Badge color="gray">Chờ xử lý</Badge>}
                    {row.status === "loading" && <Badge color="blue">Đang lấy data...</Badge>}
                    {row.status === "success" && (
                      <Badge color="green" leftSection={<IconCheck size={10} />}>
                        Thành công
                      </Badge>
                    )}
                    {row.status === "error" && (
                      <Tooltip label={row.errorMsg}>
                        <Badge color="red" leftSection={<IconX size={10} />}>
                          Lỗi
                        </Badge>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Hidden component for printing */}
      <LabelTemplate ref={printRef} labels={labelsToPrint} printConfig={{ marginLeft, marginTop, columnGap, rowGap, pageWidth, pageHeight }} />
    </Box>
  );
};

export default PrintProductLabel;
